import {
    Locale,
    ArticleCrudData,
    CMS_KEY_PLACEHOLDER,
    ProductDisplayType,
    ArticleBusinessType,
} from 'admin/articleService'
import styled from 'styled-typed'
import { PricingType } from 'products/articleConfigurationService'
import { Col } from 'uiComponents/flex'
import { OptionItemDetails } from 'products/options/optionsService'
import { t } from 'translations'

export const SectionTitle = styled.div`
    font-weight: bold;
    font-size: 1rem;
    margin-bottom: 1em;
`

export const PreviewBox = styled.div`
    margin-top: 2em;
    border: 1px solid ${(p) => p.theme.colors.border};
    width: 20em;
    border-radius: 4px;
    min-height: 10em;
`

export const RedCross = styled.span`
    color: ${(p) => p.theme.colors.status.error};
    margin-left: 0.8em;
`

export const PreviewText = styled.div`
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    display: block;
    padding: 1rem;
`

export const PreviewNumber = styled.div`
    font-size: 1.2rem;
    font-weight: bold;
    padding: 1rem;
`

export const PreviewDescription = styled.div`
    font-size: 0.75rem;
    line-height: 1.3em;
    text-overflow: ellipsis;
    overflow: hidden;
    display: block;
    padding: 1em;
`

export const Link = styled.div`
    font-size: 0.875em;
    span {
        color: ${(p) => p.theme.colors.boyBlue};
        text-decoration: underline;
        cursor: pointer;
    }
`

export const MinMaxCol = styled(Col)`
    display: flex;
    align-items: center;
`

const getRegularPricingTypeOptions = (): { name: string; value: PricingType }[] => [
    { value: 'rtp', name: t('products.crud.pricingTypes.regular') },
    { value: 'upsell', name: t('products.crud.pricingTypes.upsell') },
]

const getMembershipPricingTypeOptions = (): { name: string; value: PricingType }[] => [
    { value: 'payment_plan', name: t('products.crud.pricingTypes.paymentPlan') },
]

export const getPricingTypeOptions = (businessType: ArticleBusinessType | null) => {
    return businessType === 'membership' ? getMembershipPricingTypeOptions() : getRegularPricingTypeOptions()
}

export const getProductTypeNameMap = () => ({
    single_visit_ticket: t('products.crud.productTypes.singleVisitTicket'),
    visit_reservation: t('products.crud.productTypes.visitReservation'),
    upsell: t('products.crud.productTypes.upsell'),
    season_pass: t('products.crud.productTypes.seasonPass'),
    membership: t('products.crud.productTypes.membership'),
    multi_visit_ticket: t('products.crud.productTypes.multiVisitTicket'),
    bundle: t('products.crud.productTypes.bundle'),
    voucher: t('products.crud.productTypes.voucher'),
    service_date_change: t('products.crud.productTypes.serviceDateChange'),
    service: t('products.crud.productTypes.service'),
    service_fee: t('products.crud.productTypes.serviceFee'),
    donation: t('products.crud.productTypes.donation'),
    loyalty_card: t('products.crud.productTypes.loyaltyCard'),
    food_and_beverage: t('products.crud.productTypes.foodAndBeverage'),
})

const getAllBusinessTypeOptions = (): {
    name: string
    value: ArticleBusinessType
}[] => {
    const productTypeNameMap = getProductTypeNameMap()
    return [
        {
            value: 'single_visit_ticket',
            name: productTypeNameMap.single_visit_ticket,
        },
        { value: 'visit_reservation', name: productTypeNameMap.visit_reservation },
        { value: 'upsell', name: productTypeNameMap.upsell },
        { value: 'season_pass', name: productTypeNameMap.season_pass },
        { value: 'membership', name: productTypeNameMap.membership },
        { value: 'multi_visit_ticket', name: productTypeNameMap.multi_visit_ticket },
        {
            value: 'bundle',
            name: t('products.crud.productTypes.bundleOption'),
        },
        {
            value: 'voucher',
            name: t('products.crud.productTypes.voucherOption'),
        },
        {
            value: 'service_date_change',
            name: `${productTypeNameMap.service_date_change}`,
        },
        {
            value: 'service',
            name: t('products.crud.productTypes.serviceOption'),
        },
        { value: 'service_fee', name: `${productTypeNameMap.service_fee}` },
        { value: 'donation', name: productTypeNameMap.donation },
        { value: 'loyalty_card', name: productTypeNameMap.loyalty_card },
        { value: 'food_and_beverage', name: productTypeNameMap.food_and_beverage },
    ]
}

export const getBusinessTypeOptions = (dateChangeFeatureEnabled: boolean, membershipsFeatureEnabled: boolean) =>
    getAllBusinessTypeOptions().filter((businessType) => {
        if (businessType.value === 'service_date_change' && !dateChangeFeatureEnabled) {
            return false
        }
        if (businessType.value === 'membership' && !membershipsFeatureEnabled) {
            return false
        }
        return true
    })

export const getDisplayTypeOptions = (): { name: string; value: ProductDisplayType }[] => [
    { value: 'REGULAR_TICKET', name: t('products.crud.displayTypes.regularTicket') },
    { value: 'SUBSCRIPTION', name: t('products.crud.displayTypes.subscription') },
]

export function mapLocales(locs: Locale[], productData: ArticleCrudData) {
    const descriptionKey = productData.description ? productData.description.key : CMS_KEY_PLACEHOLDER

    const emptyObject = {
        name: {
            key: CMS_KEY_PLACEHOLDER,
            text: {},
        },
        description: {
            key: descriptionKey,
            text: {},
        },
    }
    locs.forEach((l) => {
        emptyObject.name.text[l.code] = productData.name.text[l.code] ? productData.name.text[l.code] : ''
        emptyObject.description.text[l.code] =
            productData.description && productData.description.text[l.code] ? productData.description.text[l.code] : ''
    })
    return emptyObject
}

export const mapArticleData = (data: ArticleCrudData | OptionItemDetails) => {
    if ('scanningOptions' in data && data.scanningOptions?.scanLimitType === 'number_of_scans') {
        data.scanningOptions.scanLimitType = 'scan_period_selected_date'
    }
    return data as ArticleCrudData | OptionItemDetails
}

export function extractContentFromHTML(richText: string) {
    var span = document.createElement('span')
    span.innerHTML = richText
    return span.textContent || span.innerText
}
